import React, { useEffect } from 'react';
import { Spinner, Box, Link } from '@amzn/awsui-components-console';
import { FormProvider, useForm } from 'react-hook-form';
import {
	GetOperationResponse,
	UpdateOperationResponse,
} from '@amzn/snow-data-service-typescript-client';

import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useAuthContext } from 'src/providers/AuthContext';
import { logger } from 'src/utils/logger';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import useFetchOnUserActionUsingSDS from '../../../../hooks/useFetchOnUserActionUsingSDS';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';

import EditOperationPageForm from './EditOperationPageForm';
import {
	OperationProfileFormMetadata,
	convertToUpdateOperationRequest,
	DEFAULT_OPERATION_DATA,
	convertFromOperationProfileData,
} from '../AddOperation/OperationFormGenerationUtils';

interface EditOperationPageProps {
	onCancel: () => void;
	operationId: string;
	operationName: string | null;
	applicationId: string;
}

const EditOperationPage: React.FC<EditOperationPageProps> = ({
	onCancel,
	operationId,
	operationName,
	applicationId,
}) => {
	const methods = useForm<OperationProfileFormMetadata>({
		defaultValues: DEFAULT_OPERATION_DATA,
	});

	const {
		result: { isLoading: isUpdating, data: updateData, error: updateError, flashBarItems: updateFlashBarItems },
		onClickCB: updateOperation,
	} = useFetchOnUserActionUsingSDS<UpdateOperationResponse>();
	const { userName } = useAuthContext();
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const {
		isLoading: isLoadingOp,
		data: opData,
		error: opError,
	} = useFetchForSDS<GetOperationResponse>({
		url: SDS_APIGATEWAY_ENDPOINT,
		path: operationId ? `/operation/${operationId}` : '',
		method: 'GET',
	});

	useEffect(() => {
		if (opError) {
			addNavigationError({
				message: `Failed to load operation for editing: ${opError}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}

		if (updateError) {
			logger.error(`Error updating operation: ${updateError}`);

			addNavigationError({
				message: `Failed to update operation: ${updateError}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}
	}, [opError, updateError, addNavigationError]);

	useEffect(() => {
		if (opData?.operationMetadata) {
			const formData = convertFromOperationProfileData(
				opData.operationMetadata
			);

			methods.reset(formData);
		}
	}, [opData, methods]);

	useEffect(() => {
		if (updateData?.operationId) {
			addNavigationError({
				message: (
					<>
						Operation{' '}
						<Link
							variant="primary"
							onFollow={() =>
								navigate(
									ROUTES.OPERATION_DETAILS,
									`/applicationManagement/${applicationId}/${operationId}`
								)
							}
						>
							{opData?.operationMetadata?.operationName}
						</Link>{' '}
						updated successfully!
					</>
				),
				type: 'success',
				timestamp: Date.now(),
			});

			navigate(
				ROUTES.OPERATION_DETAILS,
				`/applicationManagement/${applicationId}/${operationId}`
			);
		}
	}, [updateData, applicationId, operationId, opData?.operationMetadata?.operationName, navigate, addNavigationError]);

	const submitForm = async (data: OperationProfileFormMetadata) => {
		const updateOperationRequest = convertToUpdateOperationRequest(
			data,
			operationId,
			applicationId,
			userName || ''
		);

		await updateOperation({
			path: `/operation/${operationId}`,
			method: 'PUT',
			body: updateOperationRequest,
			url: SDS_APIGATEWAY_ENDPOINT,
		});
	};

	return isLoadingOp ? (
		<Box textAlign="center" padding="l">
			<Spinner size="large" />
		</Box>
	) : opData?.operationMetadata ? (
		<FormProvider {...methods}>
			<EditOperationPageForm
				onSubmit={submitForm}
				onCancel={onCancel}
				isLoading={isUpdating}
				flashBarItems={updateFlashBarItems}
				error={updateError}
				operationName={operationName}
			/>
		</FormProvider>
	) : null;
};

export default EditOperationPage;
